import { memo, useMemo, useEffect, useRef, useCallback } from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { useTranslation } from 'react-i18next';
import { useSettingsStore } from '@/core/store';

interface SeriesData {
    name: string;
    data: Array<{ x: string | number; y: number; [key: string]: unknown }>;
}

interface WcnApexchartProps {
    type: 'line' | 'bar' | 'area';
    series: SeriesData[];
    height?: number;
    chartId: string;
    stacked?: boolean;
    horizontal?: boolean;
    hideYAxisLabels?: boolean;
    dataLabelsEnabled?: boolean;
    dataLabelsFormatter?: (value: number, opts: any) => string | number | (string | number)[];
    xAxisType?: 'datetime' | 'category';
    xAxisCategories?: string[];
    xAxisMin?: number;
    xAxisMax?: number;
    yAxisMin?: number;
    yAxisMax?: number;
    yAxisForceNiceScale?: boolean;
    dataLabelsOffsetY?: number;
    strokeDashArray?: number[];
    strokeCurve?: 'smooth' | 'straight' | 'stepline';
    tooltipShared?: boolean;
    tooltipIntersect?: boolean;
    tooltipXFormat?: 'date' | 'datetime';
    xAxisShowTime?: boolean;
    syncChartId?: string;
}

const TOOLTIP_WIDTH = 200;
const TOOLTIP_TITLE_HEIGHT = 40;

const wcnSyncInFlight = new Set<string>();
const wcnSyncDispatchIndex = new Map<string, number>();

const wcnDetachRegistry = new Map<string, () => void>();

function decideTooltipSide(
    chartId: string,
    isBar: boolean,
    seriesIndex: number,
    dataPointIndex: number,
    w?: any
): 'right' | 'left' {
    try {
        const container = document.getElementById(chartId);
        if (!container) return 'right';
        const vw = window.innerWidth;
        const MARGIN = 8;

        if (isBar) {
            const barEl = container.querySelector(
                `.apexcharts-bar-area[index="${seriesIndex}"][j="${dataPointIndex}"]`
            );
            if (!barEl) return 'right';
            const barRect = barEl.getBoundingClientRect();
            const spaceRight = vw - barRect.right - MARGIN;
            const spaceLeft = barRect.left - MARGIN;
            if (spaceRight < TOOLTIP_WIDTH && spaceLeft >= TOOLTIP_WIDTH) return 'left';
            return 'right';
        }

        const svgEl = container.querySelector<SVGSVGElement>('.apexcharts-svg');
        const g = w?.globals;
        if (!svgEl || !g) return 'right';

        const si = seriesIndex >= 0 ? seriesIndex : 0;
        const px = (g.pointsArray as number[][][])?.[si]?.[dataPointIndex]?.[0] ?? 0;
        const svgRect = svgEl.getBoundingClientRect();
        const pointClientX = svgRect.left + (g.translateX as number) + px;

        const spaceRight = vw - pointClientX - MARGIN;
        const spaceLeft = pointClientX - MARGIN;
        if (spaceRight < TOOLTIP_WIDTH && spaceLeft >= TOOLTIP_WIDTH) return 'left';
        return 'right';
    } catch {
        return 'right';
    }
}

function buildTooltipHTML(
    header: string,
    valueRows: string,
    bg: string,
    titleBg: string,
    border: string,
    hColor: string,
    shadow: string,
    flipped: boolean
): string {
    const arrowLeft = `left:-6px; right:auto;
        border-bottom-width:1px; border-bottom-style:solid; border-bottom-color:${border};
        border-left-width:1px;   border-left-style:solid;   border-left-color:${border};
        border-top-width:0; border-right-width:0;`;
    const arrowRight = `right:-6px; left:auto;
        border-top-width:1px;   border-top-style:solid;   border-top-color:${border};
        border-right-width:1px; border-right-style:solid; border-right-color:${border};
        border-bottom-width:0; border-left-width:0;`;

    return `
        <div style="filter:${shadow};">
            <div style="
                position:relative; overflow:visible;
                border:1px solid ${border}; border-radius:3px;
                min-width:${TOOLTIP_WIDTH}px;
            ">
                <div style="
                    padding:10px 14px 8px;
                    font-weight:700; font-size:15px; color:${hColor};
                    background:${titleBg}; border-radius:3px 3px 0 0;
                    border-bottom-width:1px; border-bottom-style:solid; border-bottom-color:${border};
                ">${header}</div>
                <div style="position:relative; overflow:visible; padding:10px 14px; background:${bg}; border-radius:0 0 3px 3px;">
                    <div id="wcn-tt-arrow" data-bg="${bg}" data-border="${border}" style="
                        position:absolute; top:15px; transform:rotate(45deg);
                        width:10px; height:10px; z-index:1;
                        background:${bg};
                        ${flipped ? arrowRight : arrowLeft}
                    "></div>
                    ${valueRows}
                </div>
            </div>
        </div>`;
}

function updateArrowDirection(arrowEl: HTMLElement, flipped: boolean, border: string, bg: string) {
    arrowEl.style.background = bg;
    if (flipped) {
        arrowEl.style.left = 'auto';
        arrowEl.style.right = '-6px';
        arrowEl.style.borderTop = `1px solid ${border}`;
        arrowEl.style.borderRight = `1px solid ${border}`;
        arrowEl.style.borderBottom = '0px';
        arrowEl.style.borderLeft = '0px';
    } else {
        arrowEl.style.right = 'auto';
        arrowEl.style.left = '-6px';
        arrowEl.style.borderBottom = `1px solid ${border}`;
        arrowEl.style.borderLeft = `1px solid ${border}`;
        arrowEl.style.borderTop = '0px';
        arrowEl.style.borderRight = '0px';
    }
}

const WcnApexchart = memo(function WcnApexchart({
    type,
    series,
    height = 480,
    chartId,
    stacked = false,
    horizontal = false,
    hideYAxisLabels = false,
    dataLabelsEnabled = false,
    dataLabelsFormatter,
    xAxisType = 'datetime',
    xAxisCategories,
    xAxisMin,
    xAxisMax,
    yAxisMin,
    yAxisMax,
    yAxisForceNiceScale = true,
    dataLabelsOffsetY = 0,
    strokeDashArray = [0, 0, 0, 5, 5, 5],
    strokeCurve = 'straight',
    tooltipShared = true,
    tooltipIntersect = false,
    tooltipXFormat = 'datetime',
    xAxisShowTime = false,
    syncChartId
}: WcnApexchartProps) {
    const { t } = useTranslation();
    const { darkMode, isMobile } = useSettingsStore();

    const isBar = type === 'bar';

    const rafLoopRef = useRef<Map<string, number>>(new Map());
    const activeBarRef = useRef<Map<string, string>>(new Map());
    const lastValidIndexRef = useRef<Map<string, number>>(new Map());
    const syncChartIdRef = useRef(syncChartId);
    useEffect(() => {
        syncChartIdRef.current = syncChartId;
    }, [syncChartId]);

    const internalSeries = useMemo(() => {
        if (xAxisType !== 'datetime') return series;
        return series.map((s) => ({
            ...s,
            data: s.data.map((point) => {
                if (xAxisShowTime) {
                    return point;
                }
                const ts = typeof point.x === 'number' ? point.x : new Date(point.x as string).getTime();
                return { ...point, x: ts };
            })
        }));
    }, [series, xAxisType, xAxisShowTime]);

    const internalXAxisType = xAxisType === 'datetime' && xAxisShowTime ? 'category' : xAxisType;

    const detachTooltipObserver = useCallback(() => {
        const raf = rafLoopRef.current.get(chartId);
        if (raf) cancelAnimationFrame(raf);
        rafLoopRef.current.delete(chartId);
        activeBarRef.current.delete(chartId);
    }, [chartId]);

    const attachTooltipObserver = useCallback(
        (seriesIndex: number, dataPointIndex: number) => {
            const barKey = tooltipShared ? `${dataPointIndex}` : `${seriesIndex}-${dataPointIndex}`;

            if (activeBarRef.current.get(chartId) === barKey) return;
            activeBarRef.current.set(chartId, barKey);

            const prevRaf = rafLoopRef.current.get(chartId);
            if (prevRaf) cancelAnimationFrame(prevRaf);

            const container = document.getElementById(chartId);
            if (!container) return;

            const flippedBar = isBar
                ? decideTooltipSide(chartId, isBar, seriesIndex, dataPointIndex) === 'left'
                : false;

            let cachedTooltipEl: HTMLElement | null = null;
            let cachedArrowEl: HTMLElement | null = null;
            let cachedGlobals: any = null;

            const loop = () => {
                const tooltipEl = cachedTooltipEl ?? container.querySelector<HTMLElement>('.apexcharts-tooltip');

                if (
                    !tooltipEl ||
                    tooltipEl.style.opacity === '0' ||
                    !tooltipEl.classList.contains('apexcharts-active')
                ) {
                    cachedTooltipEl = null;
                    cachedArrowEl = null;
                    rafLoopRef.current.set(chartId, requestAnimationFrame(loop));
                    return;
                }

                cachedTooltipEl = tooltipEl;

                const canvasEl = container.querySelector<HTMLElement>('.apexcharts-canvas');
                if (!canvasEl) {
                    rafLoopRef.current.set(chartId, requestAnimationFrame(loop));
                    return;
                }
                const cRect = canvasEl.getBoundingClientRect();

                if (isBar) {
                    const barEl = tooltipShared
                        ? container.querySelector<Element>(`[j="${dataPointIndex}"].apexcharts-bar-area`)
                        : container.querySelector<Element>(
                              `.apexcharts-bar-area[index="${seriesIndex}"][j="${dataPointIndex}"]`
                          );

                    if (!barEl) {
                        rafLoopRef.current.set(chartId, requestAnimationFrame(loop));
                        return;
                    }

                    const bRect = barEl.getBoundingClientRect();

                    const rawLeft = flippedBar
                        ? bRect.left - cRect.left - TOOLTIP_WIDTH - 4
                        : bRect.right - cRect.left + 4;

                    const MARGIN = 8;
                    const minLeft = MARGIN - cRect.left;
                    const maxLeft = window.innerWidth - MARGIN - TOOLTIP_WIDTH - cRect.left;
                    tooltipEl.style.left = `${Math.min(Math.max(rawLeft, minLeft), maxLeft)}px`;

                    let targetRect = bRect;
                    if (tooltipShared) {
                        container
                            .querySelectorAll<Element>(`[j="${dataPointIndex}"].apexcharts-bar-area`)
                            .forEach((seg) => {
                                const r = seg.getBoundingClientRect();
                                if (r.top < targetRect.top) targetRect = r;
                            });
                    }

                    const ARROW_OFFSET = TOOLTIP_TITLE_HEIGHT + 15 + 5;
                    const barCenterY = targetRect.top - cRect.top + targetRect.height / 2;
                    const apexTop = Number.parseFloat(tooltipEl.style.top) || 0;
                    tooltipEl.style.transform = `translateY(${barCenterY - ARROW_OFFSET - apexTop}px)`;
                } else {
                    if (!cachedGlobals) {
                        const chartInstance = (window as any).Apex?._chartInstances?.find((c: any) => c.id === chartId);
                        cachedGlobals = chartInstance?.chart?.w?.globals ?? null;
                    }
                    const g = cachedGlobals;
                    if (!g) {
                        rafLoopRef.current.set(chartId, requestAnimationFrame(loop));
                        return;
                    }

                    const si = seriesIndex >= 0 ? seriesIndex : 0;
                    const pt = (g.pointsArray as number[][][])?.[si]?.[dataPointIndex];
                    if (!pt) {
                        rafLoopRef.current.set(chartId, requestAnimationFrame(loop));
                        return;
                    }

                    // Y positioning
                    const plotTop = (g.translateY as number) ?? 0;
                    const markerCY = plotTop + pt[1];
                    const ARROW_OFF = TOOLTIP_TITLE_HEIGHT + 15 + 5;
                    const apexTop = Number.parseFloat(tooltipEl.style.top) || 0;
                    tooltipEl.style.transform = `translateY(${markerCY - ARROW_OFF - apexTop}px)`;

                    // X positioning
                    const MARGIN = 8;
                    const plotLeft = (g.translateX as number) ?? 0;
                    const pointX = plotLeft + pt[0];
                    const spaceOnRight = (g.gridWidth as number) - pt[0];
                    const rawLeft = spaceOnRight >= TOOLTIP_WIDTH + 8 ? pointX + 4 : pointX - TOOLTIP_WIDTH - 4;
                    const minLeft = MARGIN - cRect.left;
                    const maxLeft = window.innerWidth - MARGIN - TOOLTIP_WIDTH - cRect.left;
                    const clampedLeft = Math.min(Math.max(rawLeft, minLeft), maxLeft);
                    tooltipEl.style.left = `${clampedLeft}px`;

                    const actuallyFlipped = clampedLeft + TOOLTIP_WIDTH / 2 < pointX;

                    if (!cachedArrowEl) {
                        cachedArrowEl = tooltipEl.querySelector<HTMLElement>('#wcn-tt-arrow');
                    }
                    if (cachedArrowEl) {
                        const bg = cachedArrowEl.dataset.bg ?? (darkMode ? '#262626' : '#fff');
                        const border = cachedArrowEl.dataset.border ?? (darkMode ? '#444' : '#e0e0e0');
                        updateArrowDirection(cachedArrowEl, actuallyFlipped, border, bg);
                    }
                }

                rafLoopRef.current.set(chartId, requestAnimationFrame(loop));
            };

            rafLoopRef.current.set(chartId, requestAnimationFrame(loop));
        },
        [chartId, isBar, tooltipShared, darkMode]
    );

    useEffect(() => {
        wcnDetachRegistry.set(chartId, detachTooltipObserver);
        return () => {
            wcnDetachRegistry.delete(chartId);
        };
    }, [chartId, detachTooltipObserver]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            rafLoopRef.current.forEach((id) => cancelAnimationFrame(id));
            rafLoopRef.current.clear();
            activeBarRef.current.clear();
            lastValidIndexRef.current.clear();
            wcnSyncInFlight.delete(chartId);
            wcnSyncDispatchIndex.delete(chartId);
        };
    }, [chartId]);

    const dispatchSyncToOthers = useCallback(
        (seriesIndex: number, dataPointIndex: number) => {
            const currentSyncId = syncChartIdRef.current;
            if (!currentSyncId) return;

            document.querySelectorAll<HTMLElement>(`[data-sync-chart-id="${currentSyncId}"]`).forEach((container) => {
                const otherChartId = container.id;
                if (otherChartId === chartId) return;

                const svgEl = container.querySelector<SVGSVGElement>('.apexcharts-svg');
                if (!svgEl) return;

                const chartInstance = (window as any).Apex?._chartInstances?.find((c: any) => c.id === otherChartId);
                const sg = chartInstance?.chart?.w?.globals;
                if (!sg) return;

                const si = seriesIndex >= 0 ? seriesIndex : 0;
                const pt = (sg.pointsArray as number[][][])?.[si]?.[dataPointIndex];

                wcnSyncDispatchIndex.delete(otherChartId);
                wcnSyncInFlight.delete(otherChartId);

                wcnSyncDispatchIndex.set(otherChartId, dataPointIndex);
                wcnSyncInFlight.add(otherChartId);

                const svgRect = svgEl.getBoundingClientRect();

                const cx = pt ? pt[0] : (sg.gridWidth as number) / 2;
                const cy = pt ? Math.max(pt[1], 2) : (sg.gridHeight as number) / 2;
                svgEl.dispatchEvent(
                    new MouseEvent('mousemove', {
                        bubbles: true,
                        cancelable: true,
                        clientX: svgRect.left + (sg.translateX as number) + cx,
                        clientY: svgRect.top + (sg.translateY as number) + cy
                    })
                );
            });
        },
        [chartId]
    );

    const dispatchLeaveToOthers = useCallback(() => {
        const currentSyncId = syncChartIdRef.current;
        if (!currentSyncId) return;

        document.querySelectorAll<HTMLElement>(`[data-sync-chart-id="${currentSyncId}"]`).forEach((container) => {
            const otherChartId = container.id;
            if (otherChartId === chartId) return;
            wcnDetachRegistry.get(otherChartId)?.();
            const svgEl = container.querySelector<SVGSVGElement>('.apexcharts-svg');
            if (svgEl) {
                svgEl.dispatchEvent(new MouseEvent('mouseout', { bubbles: true, cancelable: true }));
            }
        });
    }, [chartId]);

    const hasDataAtPoint = useCallback((s: number[][], dataPointIndex: number): boolean => {
        return s.some((series) => {
            const v = series[dataPointIndex];
            return v != null && !Number.isNaN(v);
        });
    }, []);

    const chartEvents = useMemo<ApexOptions['chart']>(() => {
        if (syncChartId) {
            return {
                events: {
                    mouseMove: (_e: any, chartContext: any, config: any) => {
                        // Check sync-in-flight FIRST — before any dataPointIndex guard.
                        if (wcnSyncInFlight.has(chartId)) {
                            wcnSyncInFlight.delete(chartId);
                            const intendedIndex = wcnSyncDispatchIndex.get(chartId) ?? -1;
                            wcnSyncDispatchIndex.delete(chartId);
                            if (intendedIndex >= 0) {
                                const si = config.seriesIndex >= 0 ? config.seriesIndex : 0;
                                attachTooltipObserver(si, intendedIndex);
                            }
                            return;
                        }

                        if (config.dataPointIndex < 0 || config.seriesIndex < 0) return;

                        const idx = config.dataPointIndex;
                        const s = chartContext.w.globals.series as number[][];
                        const hasData = hasDataAtPoint(s, idx);

                        if (hasData) {
                            lastValidIndexRef.current.set(chartId, idx);
                        }

                        const resolvedIndex = hasData ? idx : (lastValidIndexRef.current.get(chartId) ?? idx);

                        attachTooltipObserver(config.seriesIndex, resolvedIndex);
                        dispatchSyncToOthers(config.seriesIndex, idx);
                    },
                    dataPointMouseEnter: (
                        _e: unknown,
                        _ctx: unknown,
                        config: { seriesIndex: number; dataPointIndex: number }
                    ) => {
                        if (wcnSyncInFlight.has(chartId)) {
                            wcnSyncInFlight.delete(chartId);
                            const intendedIndex = wcnSyncDispatchIndex.get(chartId) ?? config.dataPointIndex;
                            wcnSyncDispatchIndex.delete(chartId);
                            attachTooltipObserver(config.seriesIndex, intendedIndex);
                            return;
                        }
                        attachTooltipObserver(config.seriesIndex, config.dataPointIndex);
                        dispatchSyncToOthers(config.seriesIndex, config.dataPointIndex);
                    },
                    dataPointMouseLeave: () => {
                        detachTooltipObserver();
                        dispatchLeaveToOthers();
                        lastValidIndexRef.current.delete(chartId);
                    },
                    mouseLeave: () => {
                        detachTooltipObserver();
                        dispatchLeaveToOthers();
                        lastValidIndexRef.current.delete(chartId);
                    }
                }
            };
        }

        return {
            events: {
                mouseMove: (_e: any, chartContext: any, config: any) => {
                    if (config.dataPointIndex < 0 || isBar) return;
                    const si = config.seriesIndex >= 0 ? config.seriesIndex : 0;
                    const s = chartContext.w.globals.series as number[][];
                    const hasData = hasDataAtPoint(s, config.dataPointIndex);
                    if (hasData) attachTooltipObserver(si, config.dataPointIndex);
                },
                dataPointMouseEnter: (
                    _e: unknown,
                    _ctx: unknown,
                    config: { seriesIndex: number; dataPointIndex: number }
                ) => {
                    attachTooltipObserver(config.seriesIndex, config.dataPointIndex);
                },
                dataPointMouseLeave: () => detachTooltipObserver(),
                mouseLeave: () => detachTooltipObserver()
            }
        };
    }, [
        syncChartId,
        chartId,
        isBar,
        hasDataAtPoint,
        attachTooltipObserver,
        detachTooltipObserver,
        dispatchSyncToOthers,
        dispatchLeaveToOthers
    ]);

    const tooltipCustom = useCallback(
        ({
            series: s,
            seriesIndex,
            dataPointIndex: rawDataPointIndex,
            w
        }: {
            series: number[][];
            seriesIndex: number;
            dataPointIndex: number;
            w: any;
        }): string => {
            const pendingIndex = wcnSyncDispatchIndex.get(chartId);
            const dataPointIndex = pendingIndex !== undefined ? pendingIndex : rawDataPointIndex;

            const bg = darkMode ? '#262626' : '#fff';
            const titleBg = darkMode ? 'var(--dark-sub-group)' : '#F8F8F8';
            const border = darkMode ? '#444' : '#e0e0e0';
            const hColor = darkMode ? '#fff' : '#000';
            const tColor = darkMode ? '#aaa' : '#666';
            const shadow = darkMode
                ? 'drop-shadow(0 4px 12px rgba(0,0,0,0.5))'
                : 'drop-shadow(0 4px 12px rgba(0,0,0,0.15))';

            const flipped = decideTooltipSide(chartId, isBar, seriesIndex, dataPointIndex, w) === 'left';

            // Header
            let header = '';
            if (xAxisType === 'datetime' && !xAxisShowTime) {
                const si = seriesIndex >= 0 ? seriesIndex : 0;
                const raw = w.globals.seriesX[si]?.[dataPointIndex];
                if (!raw) return '<div style="display:none"></div>';
                const date = new Date(raw);
                header =
                    tooltipXFormat === 'date'
                        ? date.toLocaleDateString('de-DE', {
                              day: 'numeric',
                              month: 'numeric',
                              year: 'numeric'
                          })
                        : date
                              .toLocaleString('de-DE', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                              })
                              .replace(',', '');
            } else {
                const cats: string[] = w.globals.categoryLabels?.length
                    ? w.globals.categoryLabels
                    : w.globals.labels?.length
                      ? w.globals.labels
                      : (w.globals.seriesX?.[0] ?? []).map(
                            (_: unknown, i: number) => w.config.series?.[0]?.data?.[i]?.x ?? ''
                        );
                header = cats[dataPointIndex] ?? '';
            }

            // Value rows
            let valueRows = '';
            if (tooltipShared) {
                (w.globals.seriesNames as string[]).forEach((name: string, idx: number) => {
                    const value = s[idx]?.[dataPointIndex];
                    if (value == null || Number.isNaN(value)) return;
                    const formatted = idx === 1 && xAxisType === 'datetime' ? value.toFixed(2) : Math.round(value);
                    const dot: string = w.globals.colors[idx] ?? '#ccc';
                    valueRows += `
                    <div style="display:flex;align-items:center;gap:8px;margin-bottom:4px;">
                        <span style="display:inline-block;width:10px;height:10px;background:${dot};flex-shrink:0;"></span>
                        <span style="color:${tColor};font-size:14px;">${name}: <strong style="color:${hColor};">${formatted}</strong></span>
                    </div>`;
                });
            } else {
                const value = s[seriesIndex]?.[dataPointIndex];
                if (value != null && !Number.isNaN(value)) {
                    const formatted =
                        seriesIndex === 1 && xAxisType === 'datetime' ? value.toFixed(2) : Math.round(value);
                    const name = w.globals.seriesNames[seriesIndex] as string;
                    const dot: string = w.globals.colors[seriesIndex] ?? '#ccc';
                    valueRows = `
                    <div style="display:flex;align-items:center;gap:8px;">
                        <span style="display:inline-block;width:10px;height:10px;background:${dot};flex-shrink:0;"></span>
                        <span style="color:${tColor};font-size:14px;">${name}: <strong style="color:${hColor};">${formatted}</strong></span>
                    </div>`;
                }
            }

            if (!valueRows.trim()) return '<div style="display:none"></div>';

            return buildTooltipHTML(header, valueRows, bg, titleBg, border, hColor, shadow, flipped);
        },
        [darkMode, chartId, isBar, xAxisType, xAxisShowTime, tooltipXFormat, tooltipShared]
    );

    const options: ApexOptions = useMemo(
        () => ({
            chart: {
                id: chartId,
                toolbar: { show: false },
                background: 'transparent',
                fontFamily: 'Wanzl_Etelka, Montserrat, sans-serif',
                ...(stacked && { stacked: true }),
                zoom: { enabled: false },
                selection: { enabled: false },
                parentHeightOffset: 0,
                animations: {
                    enabled: !isBar,
                    dynamicAnimation: { enabled: !isBar }
                },
                ...(isMobile &&
                    internalXAxisType === 'category' &&
                    !horizontal && {
                        margin: { bottom: 20 }
                    }),
                ...chartEvents
            },
            states: {
                hover: { filter: { type: 'none' } },
                active: { filter: { type: 'none' } }
            },
            colors: darkMode
                ? ['#1781A5', '#CE8E4E', '#ABB32E', '#6C8989', '#8355B7', '#6BA5A3']
                : ['#0099CD', '#F49739', '#C6D300', '#659999', '#9D60E2', '#7ACDC9'],
            theme: { mode: darkMode ? 'dark' : 'light' },
            dataLabels: {
                enabled: dataLabelsEnabled,
                ...(dataLabelsFormatter && {
                    formatter: (value: number, opts: any) => {
                        const result = dataLabelsFormatter(value, opts);
                        if (horizontal && Array.isArray(result) && result.length === 2) {
                            return `${result[0]} (${result[1]})`;
                        }
                        return result;
                    }
                }),
                style: {
                    fontSize: '13px',
                    fontWeight: '500',
                    ...(horizontal && stacked && { colors: ['#fff'] })
                },
                offsetY: dataLabelsOffsetY,
                dropShadow: { enabled: false }
            },
            plotOptions: {
                bar: {
                    horizontal,
                    barHeight: horizontal ? '85%' : undefined,
                    dataLabels: { position: 'center' }
                }
            },
            stroke: {
                curve: strokeCurve,
                dashArray: strokeDashArray,
                width: isBar ? 0 : 2
            },
            markers: {
                size: 0,
                hover: { size: 6, sizeOffset: 2 },
                shape: 'circle'
            },
            legend: {
                position: 'bottom',
                markers: {
                    shape: 'square',
                    width: isMobile ? 16 : 12,
                    height: isMobile ? 16 : 12,
                    offsetX: -4
                },
                ...(!isMobile && {
                    itemMargin: { horizontal: 9, vertical: 16 }
                })
            },
            noData: {
                text: t('noData'),
                style: {
                    fontSize: '18px',
                    color: darkMode ? '#ffffff' : '#373d3f'
                }
            },
            grid: {
                borderColor: darkMode ? '#262626' : 'var(--sidebar-divider)',
                xaxis: { lines: { show: true } },
                yaxis: { lines: { show: true } }
            },
            yaxis: {
                ...(yAxisMin !== undefined && { min: yAxisMin }),
                ...(yAxisMax !== undefined && { max: yAxisMax }),
                axisBorder: {
                    show: isMobile && !horizontal,
                    color: darkMode ? '#262626' : 'var(--sidebar-divider)',
                    strokeWidth: 0.5
                },
                forceNiceScale: yAxisForceNiceScale,
                labels: {
                    show: !hideYAxisLabels,
                    minWidth: hideYAxisLabels
                        ? 0
                        : horizontal
                          ? isMobile
                              ? 60
                              : 120
                          : internalXAxisType === 'datetime'
                            ? 20
                            : 1,
                    maxWidth: hideYAxisLabels ? 0 : horizontal ? (isMobile ? 80 : 220) : 160,
                    ...(horizontal &&
                        !hideYAxisLabels && {
                            style: { cssClass: 'apexcharts-yaxis-label-horizontal' }
                        }),
                    formatter: (val: number | string) => {
                        if (hideYAxisLabels) return '';
                        if (horizontal && typeof val === 'string') {
                            const maxChars = isMobile ? 8 : 24;
                            return val.length > maxChars ? `${val.slice(0, maxChars)}…` : val;
                        }
                        if (val != null && typeof val === 'number' && !Number.isNaN(val)) {
                            return val.toFixed(0);
                        }
                        return String(val);
                    }
                }
            },
            xaxis: {
                type: internalXAxisType,
                axisBorder: {
                    show: true,
                    color: darkMode ? '#262626' : 'var(--sidebar-divider)',
                    strokeWidth: 0.5,
                    offsetX: isMobile && !horizontal ? -50 : 0
                },
                axisTicks: {
                    show: true,
                    color: darkMode ? '#262626' : 'var(--sidebar-divider)'
                },
                ...(xAxisCategories && { categories: xAxisCategories }),
                ...(xAxisMin !== undefined && { min: xAxisMin }),
                ...(xAxisMax !== undefined && { max: xAxisMax }),
                ...(internalXAxisType === 'datetime' && {
                    labels: {
                        datetimeUTC: false,
                        datetimeFormatter: {
                            year: 'yyyy',
                            month: 'M.yyyy',
                            day: 'd.M',
                            hour: 'HH:mm'
                        }
                    }
                }),
                ...(internalXAxisType === 'category' && {
                    labels: xAxisShowTime
                        ? {
                              rotate: -45,
                              rotateAlways: false,
                              hideOverlappingLabels: true,
                              trim: false,
                              style: { fontSize: '10px' }
                          }
                        : {
                              rotate: -45,
                              rotateAlways: isMobile,
                              trim: false,
                              hideOverlappingLabels: false,
                              ...(isMobile && { offsetY: 5 })
                          }
                }),
                crosshairs: { show: !isBar },
                tooltip: { enabled: false }
            },
            tooltip: {
                shared: tooltipShared,
                intersect: isBar ? !tooltipShared : (tooltipIntersect ?? false),
                followCursor: false,
                custom: tooltipCustom
            }
        }),
        [
            darkMode,
            t,
            chartId,
            stacked,
            horizontal,
            hideYAxisLabels,
            dataLabelsEnabled,
            dataLabelsFormatter,
            xAxisType,
            internalXAxisType,
            xAxisShowTime,
            xAxisCategories,
            xAxisMin,
            xAxisMax,
            yAxisMin,
            yAxisMax,
            yAxisForceNiceScale,
            dataLabelsOffsetY,
            isMobile,
            strokeDashArray,
            strokeCurve,
            tooltipShared,
            tooltipIntersect,
            tooltipXFormat,
            isBar,
            chartEvents,
            tooltipCustom
        ]
    );

    return (
        <div
            id={chartId}
            className={['wcn-apexchart', horizontal ? 'wcn-apexchart--horizontal' : ''].filter(Boolean).join(' ')}
            {...(syncChartId && { 'data-sync-chart-id': syncChartId })}
        >
            <ReactApexChart key={chartId} type={type} height={height} options={options} series={internalSeries} />
        </div>
    );
});

export default WcnApexchart;
